/**
 * Sensor Analysis Utilities
 * Ported from React Native app for consistent status detection
 */

// Define status types
export const SensorStatus = {
  RISING: 'RISING',
  FALLING: 'FALLING', 
  STEADY: 'STEADY',
  UNKNOWN: 'UNKNOWN'
};

// Thresholds for determining status (same as React Native app)
const THRESHOLDS = {
  TEMPERATURE: {
    SIGNIFICANT_CHANGE: 0.5, // °C change to be considered significant
  },
  HUMIDITY: {
    SIGNIFICANT_CHANGE: 2, // % change to be considered significant
  },
};

// Function to get time window based on hours
export function getTimeWindow(hours) {
  return hours * 60 * 60 * 1000; // Convert hours to milliseconds
}

// Calculate the rate of change between two readings
function calculateRateOfChange(current, previous) {
  const valueDiff = current.value - previous.value;
  const currentTime = current.timestamp instanceof Date ? current.timestamp : new Date(current.timestamp);
  const previousTime = previous.timestamp instanceof Date ? previous.timestamp : new Date(previous.timestamp);
  const timeDiffMs = currentTime.getTime() - previousTime.getTime();
  return {
    value: valueDiff,
    timeDiffMs,
  };
}

// Determine status based on rate of change (ported from React Native)
export function determineStatus(readings, type = 'TEMPERATURE', timeWindowHours = 6) {
  if (!readings || readings.length < 2) {
    return SensorStatus.UNKNOWN;
  }

  const threshold = THRESHOLDS[type]?.SIGNIFICANT_CHANGE || 1;
  const windowMs = getTimeWindow(timeWindowHours);

  // Filter readings within the time window
  const now = new Date();
  const recentReadings = readings.filter((r) => {
    const timestamp = r.timestamp instanceof Date ? r.timestamp : new Date(r.timestamp);
    return now.getTime() - timestamp.getTime() <= windowMs;
  });

  // If we don't have enough recent readings, check if we have any recent data at all
  if (recentReadings.length < 2) {
    // Check if we have any readings within the last 2 hours
    const twoHoursAgo = now.getTime() - (2 * 60 * 60 * 1000);
    const veryRecentReadings = readings.filter((r) => {
      const timestamp = r.timestamp instanceof Date ? r.timestamp : new Date(r.timestamp);
      return timestamp.getTime() >= twoHoursAgo;
    });
    
    // If we have recent data (within 2 hours), assume STEADY
    if (veryRecentReadings.length > 0) {
      return SensorStatus.STEADY;
    }
    
    return SensorStatus.UNKNOWN;
  }

  // Sort by timestamp (oldest first)
  const sortedReadings = [...recentReadings].sort((a, b) => {
    const timeA = a.timestamp instanceof Date ? a.timestamp : new Date(a.timestamp);
    const timeB = b.timestamp instanceof Date ? b.timestamp : new Date(b.timestamp);
    return timeA.getTime() - timeB.getTime();
  });

  // Calculate rate of change per hour
  const first = sortedReadings[0];
  const last = sortedReadings[sortedReadings.length - 1];
  const { value: totalChange, timeDiffMs } = calculateRateOfChange(last, first);
  
  // Convert to rate per hour
  const hoursDiff = timeDiffMs / (1000 * 60 * 60);
  const ratePerHour = totalChange / hoursDiff;

  // Determine status based on rate per hour
  if (Math.abs(ratePerHour) < threshold) {
    return SensorStatus.STEADY;
  }
  return ratePerHour > 0 ? SensorStatus.RISING : SensorStatus.FALLING;
}

// Helper function to get status color (same as web app)
export function getStatusColor(status) {
  switch (status) {
    case SensorStatus.RISING:
      return '#ff4d4f'; // Red for rising
    case SensorStatus.FALLING:
      return '#52c41a'; // Green for falling
    case SensorStatus.STEADY:
      return '#faad14'; // Yellow/orange for steady
    case SensorStatus.UNKNOWN:
    default:
      return '#8c8c8c'; // Gray for unknown
  }
}

// Helper function to get status text
export function getStatusText(status) {
  switch (status) {
    case SensorStatus.RISING:
      return 'RISING >';
    case SensorStatus.FALLING:
      return '< FALLING';
    case SensorStatus.STEADY:
      return 'STEADY';
    case SensorStatus.UNKNOWN:
    default:
      return 'CALCULATING';
  }
}

// Helper function to get status description
export function getStatusDescription(status) {
  switch (status) {
    case SensorStatus.RISING:
      return 'Increasing trend detected';
    case SensorStatus.FALLING:
      return 'Decreasing trend detected';
    case SensorStatus.STEADY:
      return 'Stable readings';
    case SensorStatus.UNKNOWN:
    default:
      return 'Insufficient data for trend analysis';
  }
}

// Analyze sensor readings from database format
export function analyzeSensorReadings(readings, type = 'TEMPERATURE') {
  if (!readings || readings.length === 0) {
    return {
      status: SensorStatus.UNKNOWN,
      trend: null,
      confidence: 'low'
    };
  }

  // Convert database readings to analysis format
  const analysisReadings = readings
    .filter(r => r.recorded_at && (r.temperature !== null || r.humidity !== null))
    .map(r => ({
      value: type === 'TEMPERATURE' ? r.temperature : r.humidity,
      timestamp: new Date(r.recorded_at)
    }))
    .filter(r => r.value !== null && !isNaN(r.value));

  if (analysisReadings.length < 2) {
    return {
      status: SensorStatus.UNKNOWN,
      trend: null,
      confidence: 'low'
    };
  }

  const status = determineStatus(analysisReadings, type);
  
  // Calculate confidence based on data points and time span
  const confidence = analysisReadings.length >= 5 ? 'high' : 
                     analysisReadings.length >= 3 ? 'medium' : 'low';

  return {
    status,
    trend: status.toLowerCase(),
    confidence,
    dataPoints: analysisReadings.length
  };
}
